import { valibotResolver } from "@hookform/resolvers/valibot";
import { PlusCircle } from "@phosphor-icons/react";
import { type ReactNode, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import useAllLabels from "features/labels/datahooks/useAllLabels";
import useAllLabelsMutations from "features/labels/datahooks/useAllLabelsMutations";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuFooter,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import { getSchemaMaxLength } from "common/helpers/utils";

import { LabelSchema, type LabelSchemaType } from "../schemas";
import LabelColorSelection from "./Labels/LabelColorSelection";
import LabelsList from "./Labels/LabelsList";

import type { Label } from "../types";

function sortLabels(allLabels: Label[], labels: Label[]) {
  return [
    ...[...labels].sort((label1, label2) =>
      label1.name.localeCompare(label2.name, undefined, { numeric: true }),
    ),
    ...allLabels
      .filter(({ id }) => !labels.find((existing) => existing.id === id))
      .sort((label1, label2) =>
        label1.name.localeCompare(label2.name, undefined, { numeric: true }),
      ),
  ];
}

export interface LabelsDropdownMenuProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  labels: Label[];
  toggleLabel: (label: Label, isSelected: boolean) => void;
  children: ReactNode;
  onAddLabel?: (label: Label) => Promise<void>;
}

export default function LabelsDropdownMenu({
  labels,
  toggleLabel,
  onAddLabel,
  children,
  isOpen,
  setIsOpen,
}: LabelsDropdownMenuProps) {
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const { handleSubmit, setValue, register, watch, reset } =
    useForm<LabelSchemaType>({
      defaultValues: { name: "" },
      resolver: valibotResolver(LabelSchema),
    });
  const { allLabels } = useAllLabels("workspace");
  const { createLabel } = useAllLabelsMutations("workspace");

  // we use a state here to prevent layout shifting, by updating it after the close animation is finished
  const [sortedLabels, setSortedLabels] = useState<Label[]>([]);

  useEffect(() => {
    if (allLabels) {
      setSortedLabels(sortLabels(allLabels, labels));
    }
  }, [allLabels]);

  // if labels or workspace labels change when closed, sort the list according to selection,
  // the delay ensures that it's sorted after the close animation
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setSortedLabels(sortLabels(allLabels ?? [], labels));
      }, 300);
    }
  }, [isOpen, labels, allLabels]);

  useEffect(() => {
    if (!isOpen && !isCreatingNew) {
      setTimeout(reset, 300);
    }
  }, [isOpen]);

  function createAndAddLabel(label: LabelSchemaType) {
    createLabel(label).then((newLabel) => {
      onAddLabel(newLabel).then(() => {
        setIsCreatingNew(false);
        setTimeout(reset, 300);
      });
    });
  }

  const labelName = watch("name");
  const filteredLabels = sortedLabels.filter(({ name }) =>
    labelName ? name.toLowerCase().includes(labelName.toLowerCase()) : true,
  );

  return (
    <>
      <DropdownMenu
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) setIsOpen(false);
        }}
      >
        <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>

        <DropdownMenuContent
          className="w-[max(var(--radix-popper-anchor-width),200px)]"
          size="sm"
        >
          <MenuSearchInput
            {...register("name")}
            placeholder={allLabels?.length ? "Search labels" : "Add new label"}
            maxLength={getSchemaMaxLength(LabelSchema.entries.name)}
          />

          <DropdownMenuList>
            <LabelsList
              toggleLabel={toggleLabel}
              labels={filteredLabels}
              selectedLabels={labels}
              closePopup={() => setIsOpen(false)}
            />
          </DropdownMenuList>

          {labelName &&
            !filteredLabels.find(({ name }) => name === labelName) && (
              <DropdownMenuFooter>
                <DropdownMenuItem onSelect={() => setIsCreatingNew(true)}>
                  <PlusCircle weight="fill" />
                  Create a new Label '{labelName}'
                </DropdownMenuItem>
              </DropdownMenuFooter>
            )}
        </DropdownMenuContent>
      </DropdownMenu>

      <LabelColorSelection
        labelName={labelName}
        isOpen={isCreatingNew}
        setIsOpen={setIsCreatingNew}
        onColorSelect={(color) => {
          setValue("color", color);
          handleSubmit(createAndAddLabel)();
        }}
      />
    </>
  );
}
